import React, { useCallback, useMemo, useState } from 'react';
import { compareAsc } from 'date-fns';
import { gql, useQuery } from '@apollo/client';
import { clone } from 'ramda';
import { useSessionStorage } from '#hooks/useSessionStorage';
import PageContext, {
  Peer,
  SortItem,
  impactProducts,
  defaultImpactReleaseDatesObject,
  ImpactProduct,
  ImpactProductDates,
} from '../contexts/PageQueryContext';
import {
  WurDashboardPageQueryQuery,
  GetReleaseDatesQueryQuery,
} from '#types/generated/graphql';
import { useProducts } from '#core/Products';
import { useAuth } from '#core/Auth';
import { useInstitution } from '#core/Institution';
import ErrorBoundary from '#coreComponents/ErrorBoundary';
import ErrorBoundaryFooter from '#coreComponents/ErrorBoundary/ErrorBoundaryFooter';

type ReleaseDateEntry = NonNullable<
  GetReleaseDatesQueryQuery['releaseDates']
>[number];

type MappingKeys =
  | 'releaseDates'
  | 'embargoEndDates'
  | 'isAfterReleaseDate'
  | 'isAfterEmbargoDate'
  | 'isAfterFreeEmbargoDate'
  | 'isAfterWurDetailsEmbargoDate';

const releaseDatesMap = new Map<MappingKeys, keyof ReleaseDateEntry>([
  ['releaseDates', 'releaseDate'],
  ['embargoEndDates', 'embargoedUntilDate'],
]);

const releaseDatesMapBooleans = new Map<MappingKeys, keyof ReleaseDateEntry>([
  ['isAfterReleaseDate', 'releaseDate'],
  ['isAfterEmbargoDate', 'embargoedUntilDate'],
  ['isAfterFreeEmbargoDate', 'freeEmbargoedDate'],
  ['isAfterWurDetailsEmbargoDate', 'wurDetailsEmbargoedDate'],
]);

export const GetWurDashboardPageQuery = gql`
  query WurDashboardPageQuery(
    $releaseNames: [String]!
    $responseFields: [ResponseFields2ListItem]
  ) {
    releases(releaseNames: $releaseNames, responseFields: $responseFields) {
      year
      releaseDate
      embargoEndDate
      wurDetailsEmbargoStartDate
      freeEmbargoStartDate
      locations {
        countries
        continents
      }
    }
  }
`;

export const GetReleaseDatesQuery = gql`
  query GetReleaseDatesQuery($releaseNames: [String]!, $years: [Int]) {
    releaseDates(releaseNames: $releaseNames, years: $years) {
      year
      datasetType
      releaseDate
      embargoedUntilDate
      wurDetailsEmbargoedDate
      freeEmbargoedDate
    }
  }
`;

/* TODO: this PageQuery component is specific to WUR Dashboard and needs to be
  split from the page query core component and moved inside the WUR Dashboard feature folder structure.
  Any other pages that need to use the concept of page query need their own specific page query component */
const PageQuery: React.FC<{}> = ({ children }) => {
  const productName = 'DataPlus';
  const wurInterimProductName = 'WurReporterInterim';
  const activeProduct = window.location.pathname.includes('wur-reporter')
    ? wurInterimProductName
    : productName;
  const { productPeers, productGroups } = useProducts();
  const { institution } = useInstitution();
  const selectedProduct = institution?.products.find(
    (product) => product.name === activeProduct,
  );
  const productLocations = selectedProduct?.locations;

  const wurInterimPeers = useMemo(() => productPeers(wurInterimProductName), [
    productPeers,
  ]);

  const { useElevatedPermissions } = useAuth();

  const peers = useMemo(() => productPeers(productName), [productPeers]);
  const groups = useMemo(() => productGroups(productName), [productGroups]);
  const [selectedYearIndex, setSelectedYearIndex] = useSessionStorage<number>(
    'selectedYearIndex',
    0,
  );
  const [selectedPeers, setSelectedPeers] = useSessionStorage<Peer[]>(
    'selectedPeers',
    peers,
  );

  const [
    selectedWurInterimPeers,
    setSelectedWurInterimPeers,
  ] = useSessionStorage<Peer[]>('selectedWurInterimPeers', wurInterimPeers);

  const [
    selectedGroupAnalysisPeers,
    setSelectedGroupAnalysisPeers,
  ] = useSessionStorage<Peer[]>('selectedGroupAnalysisPeers', peers);

  const [
    selectedQueryBuilderPeers,
    setSelectedQueryBuilderPeers,
  ] = useSessionStorage<Peer[]>('selectedQueryBuilderPeers', []);
  const [selectedGroup, setSelectedGroup] = useSessionStorage<
    string | undefined
  >('selectedGroup', undefined);
  const [querySelector, setQuerySelector] = useSessionStorage<object>(
    'querySelector',
    {},
  );
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const [sortItems, setSortItems] = useState<SortItem[]>([]);
  const [sortBy, setSortBy] = useState<undefined | string>();
  const [sortDirection, setSortDirection] = useState<undefined | string>();
  const [scoresRegion, setScoresRegion] = useState<string>('Worldwide');

  const { data: wurData, loading: loadingWur, error: errorWur } = useQuery<
    WurDashboardPageQueryQuery
  >(GetWurDashboardPageQuery, {
    variables: {
      releaseNames: ['wur'],
      responseFields: ['LOCATIONS'],
    },
  });

  const {
    data: impactData,
    loading: loadingImpact,
    error: errorImpact,
  } = useQuery<GetReleaseDatesQueryQuery>(GetReleaseDatesQuery, {
    variables: {
      releaseNames: impactProducts,
    },
  });

  const { releases: wurReleases } = wurData || {};

  // Using the default release date object created in the context file as a base for the object structure,
  // we create a new impactReleaseDates object from the data retruned by the GRA.
  // This object has all the details for each impact product and their release dates
  const impactReleaseDates = useMemo(() => {
    if (!impactData?.releaseDates) return defaultImpactReleaseDatesObject;

    const releaseDatesObject: ImpactProductDates = clone(
      defaultImpactReleaseDatesObject,
    );

    impactData.releaseDates.forEach((release) => {
      const product = release.datasetType as ImpactProduct;

      if (releaseDatesObject[product]) {
        const year = release.year;
        releaseDatesMap.forEach((dataKey, targetKey) => {
          const releaseDate = release[dataKey];
          if (releaseDate) {
            releaseDatesObject[product][targetKey][year] = new Date(
              releaseDate,
            );
          }
        });
        releaseDatesMapBooleans.forEach((dataKey, targetKey) => {
          const releaseDate = release[dataKey];
          if (releaseDate) {
            releaseDatesObject[product][targetKey][year] =
              compareAsc(new Date(), new Date(releaseDate)) === 1;
          }
        });
      }
    });

    return releaseDatesObject;
  }, [impactData]);

  const years = useMemo(() => {
    if (!wurReleases) {
      return [];
    }
    return wurReleases
      .reduce<number[]>((carryYears, release) => {
        if (!release) {
          return carryYears;
        }
        const { year } = release;
        if (year) {
          return [...carryYears, year];
        }
        return carryYears;
      }, [])
      .sort((a, b) => b - a);
  }, [wurReleases]);

  const releaseDates = useMemo(() => {
    if (!wurReleases) {
      return {};
    }
    return wurReleases.reduce<{ [key: string]: Date }>((acc, release) => {
      if (!release) {
        return acc;
      }
      const { year, releaseDate } = release;
      if (year) {
        return { ...acc, [year]: new Date(releaseDate) };
      }
      return acc;
    }, {});
  }, [wurReleases]);

  const embargoEndDates = useMemo(() => {
    if (!wurReleases) {
      return {};
    }
    return wurReleases.reduce<{ [key: string]: Date }>((acc, release) => {
      if (!release) {
        return acc;
      }
      const { year, embargoEndDate } = release;
      if (year) {
        return { ...acc, [year]: new Date(embargoEndDate) };
      }
      return acc;
    }, {});
  }, [wurReleases]);

  const isAfterReleaseDate = useMemo(() => {
    if (!wurReleases) {
      return {};
    }
    return wurReleases.reduce<{ [key: string]: boolean }>((acc, release) => {
      if (!release) {
        return acc;
      }
      const { year, releaseDate } = release;
      if (year) {
        return {
          ...acc,
          [year]: compareAsc(new Date(), new Date(releaseDate)) === 1,
        };
      }
      return acc;
    }, {});
  }, [wurReleases]);

  const isAfterEmbargoDate = useMemo(() => {
    if (!wurReleases) {
      return {};
    }
    return wurReleases.reduce<{ [key: string]: boolean }>((acc, release) => {
      if (!release) {
        return acc;
      }
      const { year, embargoEndDate } = release;
      if (year) {
        return {
          ...acc,
          [year]: compareAsc(new Date(), new Date(embargoEndDate)) === 1,
        };
      }
      return acc;
    }, {});
  }, [wurReleases]);

  const isAfterFreeEmbargoDate = useMemo(() => {
    if (!wurReleases) {
      return {};
    }
    return wurReleases.reduce<{ [key: string]: boolean }>((acc, release) => {
      if (!release) {
        return acc;
      }
      const { year, freeEmbargoStartDate } = release;
      if (year) {
        return {
          ...acc,
          [year]: compareAsc(new Date(), new Date(freeEmbargoStartDate)) === 1,
        };
      }
      return acc;
    }, {});
  }, [wurReleases]);

  const isAfterWurDetailsEmbargoDate = useMemo(() => {
    if (!wurReleases) {
      return {};
    }
    return wurReleases.reduce<{ [key: string]: boolean }>((acc, release) => {
      if (!release) {
        return acc;
      }
      const { year, wurDetailsEmbargoStartDate } = release;
      if (year && wurDetailsEmbargoStartDate) {
        return {
          ...acc,
          [year]: useElevatedPermissions
            ? true
            : compareAsc(new Date(), new Date(wurDetailsEmbargoStartDate)) ===
              1,
        };
      }
      return acc;
    }, {});
  }, [wurReleases, useElevatedPermissions]);

  const setSelectedYear = useCallback(
    (year: number) => {
      const yearIndex = years.indexOf(year);
      if (yearIndex < 0) {
        throw new Error(`Year (${year}) must be one of ${years.join(',')}`);
      }
      setSelectedYearIndex(yearIndex);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [years],
  );

  if (loadingWur || loadingImpact) {
    return null;
  }

  if (!wurData || !impactData || errorWur || errorImpact) {
    throw new Error('There was an error while getting Dashboard data');
  }

  const selectedRelease = wurData.releases.find(
    (release) => release.year === years[selectedYearIndex],
  );
  const releaseLocations = selectedRelease?.locations;

  const continents =
    productLocations?.continents.filter((continent) =>
      releaseLocations?.continents.includes(continent),
    ) || [];

  const countries =
    productLocations?.countries.filter((country) =>
      releaseLocations?.countries.includes(country),
    ) || [];

  return (
    <ErrorBoundary
      showDetailedErrorScreen
      footerComponent={ErrorBoundaryFooter}
      id="wur-page-query"
    >
      <PageContext.Provider
        value={{
          query: {
            selectedYear: years[selectedYearIndex],
            years,
            releaseDates,
            embargoEndDates,
            isAfterReleaseDate,
            isAfterEmbargoDate,
            isAfterFreeEmbargoDate,
            isAfterWurDetailsEmbargoDate,
            impactReleaseDates,
            querySelector,
            selectedPeers,
            selectedWurInterimPeers,
            selectedGroupAnalysisPeers,
            selectedQueryBuilderPeers,
            peers,
            wurInterimPeers,
            selectedGroup,
            groups,
            continents,
            countries,
            downloadInProgress,
            sortItems,
            sortBy,
            sortDirection,
            scoresRegion,
          },
          setSelectedYear,
          setQuerySelector,
          setSelectedPeers,
          setSelectedWurInterimPeers,
          setSelectedGroupAnalysisPeers,
          setSelectedQueryBuilderPeers,
          setSelectedGroup,
          setDownloadInProgress,
          setSortItems,
          setSortBy,
          setSortDirection,
          setScoresRegion,
        }}
      >
        {children}
      </PageContext.Provider>
    </ErrorBoundary>
  );
};

export default PageQuery;
